/**
 * Centralized Legal & Brand Configuration
 * Authoritative operating entity: Getpad Inc.
 * Brand name: Adiosoft
 * 
 * Rules:
 * - Do NOT fabricate real company address, tax number, registration number or phones if not provided in repo.
 * - Explicit placeholders are preserved until completed by legal counsel or administration.
 * - Missing fields trigger an advisory warning in Super Admin legal settings.
 */

export interface LegalConfig {
  companyName: string;
  brandName: string;
  relationshipStatement: string;
  registeredOffice: string;
  registrationCountry: string;
  registrationNumber: string;
  taxNumber: string;
  email: string;
  phone: string;
  privacyEmail: string;
  effectiveDate: string;
  lastUpdated: string;
  version: string;
  termsVersion: string;
  privacyVersion: string;
  kvkkVersion: string;
  cookieVersion: string;
  distanceSalesVersion: string;
  platformUrl: string;
  jurisdiction: string;
}

export const legalConfig: LegalConfig = {
  companyName: 'Getpad Inc.',
  brandName: 'Adiosoft',
  relationshipStatement: 'Adiosoft, Getpad Inc. bünyesinde sunulan dijital hizmet ve bulut tabanlı yazılım platformudur.',
  registeredOffice: '[GETPAD INC. RESMİ ADRESİ]',
  registrationCountry: '[GETPAD INC. KAYIT ÜLKESİ]',
  registrationNumber: '[RESMİ KAYIT NUMARASI]',
  taxNumber: '[RESMİ VERGİ NUMARASI]',
  email: '[RESMİ E-POSTA]',
  phone: '[RESMİ TELEFON]',
  privacyEmail: '[RESMİ KVKK/GİZLİLİK E-POSTASI]',
  effectiveDate: '01.01.2026',
  lastUpdated: '01.01.2026',
  version: 'v1.0',
  termsVersion: 'terms_v1.0',
  privacyVersion: 'privacy_v1.0',
  kvkkVersion: 'kvkk_v1.0',
  cookieVersion: 'cookie_v1.0',
  distanceSalesVersion: 'dist_sales_v1.0',
  platformUrl: 'https://www.adiosoft.com',
  jurisdiction: 'Getpad Inc. şirket merkezi ve 6502 sayılı Kanun uyarınca yetkili Tüketici Hakem Heyetleri ile Tüketici Mahkemeleri',
};

/**
 * Checks whether a given field contains an unfulfilled placeholder
 */
export function isFieldPlaceholder(val?: string): boolean {
  if (!val) return true;
  const str = val.trim();
  return str.startsWith('[') && str.endsWith(']');
}

/**
 * Returns list of missing mandatory legal fields that still have placeholders
 */
export function getMissingLegalFields(config: LegalConfig = legalConfig): { key: keyof LegalConfig; label: string; value: string }[] {
  const mandatoryFields: { key: keyof LegalConfig; label: string }[] = [
    { key: 'registeredOffice', label: 'Şirket Resmi Adresi' },
    { key: 'registrationCountry', label: 'Kayıt Ülkesi' },
    { key: 'registrationNumber', label: 'Resmi Şirket Kayıt / Sicil No' },
    { key: 'taxNumber', label: 'Vergi Numarası / Vergi Dairesi' },
    { key: 'email', label: 'Resmi İletişim E-Postası' },
    { key: 'privacyEmail', label: 'KVKK / Gizlilik İletişim E-Postası' },
    { key: 'phone', label: 'Resmi Telefon Numarası' },
  ];

  return mandatoryFields
    .filter((f) => isFieldPlaceholder(config[f.key] as string))
    .map((f) => ({
      key: f.key,
      label: f.label,
      value: config[f.key] as string,
    }));
}

export function hasPendingPlaceholders(config: LegalConfig = legalConfig): boolean {
  return getMissingLegalFields(config).length > 0;
}
